<div class="chat-main-window" role="region" aria-labelledby="chat-title">
  @if (!showLoader) {
    <div class="chat-inner-container" [class.attachments]="hasAttachmentsToAdd">
      <div class="close-button-wrapper">
        <span id="chat-title" class="close-text">
          {{ 'CHAT.HOW_CAN_I_HELP' | translate }}
        </span>
        <button
          class="close-button"
          type="button"
          (click)="goBack()"
          [attr.aria-label]="'COMMON.ACTION.CLOSE' | translate">
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      </div>
      <div
        class="chat-messages-container"
        #chatMessagesContainer
        role="log"
        [attr.aria-live]="'polite'"
        [attr.aria-relevant]="'additions'"
        [attr.aria-atomic]="false">
        @if ((chatMessages$ | async)?.length) {
          @for (chatMessage of chatMessages$ | async; track chatMessage; let chatMessageIndex = $index) {
            <div
              class="chat-message-wrapper"
              [class.to-the-right]="chatMessage.iamAuthor"
              #chatMessage
              role="article"
              [attr.aria-labelledby]="'msg-sr-' + chatMessageIndex">
              <span class="sr-only" [id]="'msg-sr-' + chatMessageIndex">
                {{ chatMessage.authorName }}
                {{ 'CHAT.WROTE' | translate }}: {{ chatMessage.content }}.
                @if (chatMessage.attachments?.length) {
                  {{ 'COMMON.LABEL.ATTACHMENTS' | translate }}:
                  @for (attachment of chatMessage.attachments; track attachment) {
                    {{ attachment.attachment.fileOriginalName }}
                  }
                }
              </span>
              @if (!chatMessage.iamAuthor) {
                <div
                  class="author-avatar"
                  nz-tooltip
                  [nzTooltipTitle]="chatMessage.authorName"
                  [attr.aria-hidden]="true">
                  <app-cr-avatar [userName]="chatMessage.authorName" [theme]="'THEME-2'"></app-cr-avatar>
                </div>
              }
              <div
                class="chat-message-content"
                aria-hidden="true"
                matTooltip="{{ chatMessage.createdAt | date: 'dd.MM.yyyy HH:mm' }}"
                matTooltipClass="chatTooltip"
                matTooltipPosition="left"
                matTooltipShowDelay="350"
                [ngClass]="chatMessage.iamAuthor ? 'author-messages' : 'interlocutor-messages'"
                [attr.data-cy]="'message-' + chatMessageIndex">
                <div [innerHTML]="chatMessage.content | linebreaks"></div>
                @if (chatMessage.attachments?.length) {
                  <div class="message-attachments-wrapper">
                    @for (attachment of chatMessage.attachments; track attachment) {
                      <button
                        class="message-attachment-item"
                        type="button"
                        (click)="
                          downloadMessageAttachment(
                            chatMessage.id,
                            attachment.id,
                            attachment.attachment.fileOriginalName
                          )
                        "
                        [attr.aria-label]="
                          ('CHAT.DOWNLOAD_ATTACHMENT' | translate) + ' ' + attachment.attachment.fileOriginalName
                        ">
                        <span class="attachment-file-icon" nz-icon nzType="paper-clip" nzTheme="outline"></span>
                        <span class="attachment-file-name">{{ attachment.attachment.fileOriginalName }}</span>
                      </button>
                    }
                  </div>
                }
              </div>
              @if (chatMessage.iamAuthor) {
                <div
                  class="author-avatar"
                  nz-tooltip
                  [nzTooltipTitle]="chatMessage.authorName"
                  [attr.aria-hidden]="true">
                  <app-cr-avatar [userName]="chatMessage.authorName" [theme]="'THEME-1'"></app-cr-avatar>
                </div>
              }
            </div>
          }
        } @else {
          <div class="no-messages-wrapper">
            <h2>{{ 'CHAT.NO_MESSAGES_IN_CHAT' | translate }}</h2>
          </div>
        }
      </div>
      @if (loading$ | async) {
        <div class="absolute-loader-container" role="status" aria-live="polite">
          <span class="sr-only">
            {{ 'COMMON.LAYOUT.LOADING' | translate }}
          </span>
          <mat-spinner [diameter]="35" [attr.aria-hidden]="true"> </mat-spinner>
        </div>
      }
      <div class="chat-message-input-wrapper standard-form-field">
        <mat-form-field class="chat-message-content-input" appearance="outline" subscriptSizing="dynamic">
          <textarea
            matInput
            #chatMessageInput
            id="chat-message-input-in-modal"
            #autosize="cdkTextareaAutosize"
            cdkTextareaAutosize
            cdkAutosizeMinRows="1"
            cdkAutosizeMaxRows="7"
            autocomplete="off"
            autocapitalize="off"
            [(ngModel)]="newMessageContent"
            (focus)="onChatMessageInputFocus()"
            (keydown.enter)="submitMessage($event)"
            placeholder="{{ 'CHAT.ENTER_YOUR_MESSAGE' | translate }}"
            [attr.aria-label]="'CHAT.ENTER_YOUR_MESSAGE' | translate"
            maxlength="1500"
            minlength="1"
            [attr.aria-describedby]="'chat-message-error'">
          </textarea>
          <!-- ERROR -->
          @if (newMessageContent && !messageContentValid(newMessageContent)) {
            <div id="chat-message-error">
              {{ 'CHAT.FORBIDDEN_CHARACTERS_USED' | translate }}
            </div>
          }
        </mat-form-field>
        <button
          [disabled]="
            uploadingAttachments ||
            (messageAttachments.length === 0 && (!newMessageContent || !messageContentValid(newMessageContent)))
          "
          mat-icon-button
          color="primary"
          [attr.aria-busy]="uploadingAttachments"
          [attr.aria-label]="'CHAT.SEND_MESSAGE' | translate"
          (click)="submitMessage($event)">
          @if (uploadingAttachments) {
            <mat-spinner [diameter]="22" [attr.aria-hidden]="true"></mat-spinner>
          } @else {
            <mat-icon>send</mat-icon>
          }
        </button>
        <nz-badge [nzCount]="messageAttachments?.length">
          <button
            type="button"
            nz-button
            nzType="text"
            nzSize="default"
            nzShape="circle"
            (click)="openFilePicker()"
            [attr.aria-label]="'CHAT.ADD_ATTACHMENT' | translate">
            <span nz-icon nzType="paper-clip" nzTheme="outline"></span>
          </button>
        </nz-badge>
      </div>
    </div>
  } @else {
    @if (showLoader) {
      <div class="loader-container" role="status" aria-live="polite">
        <span class="sr-only">
          {{ 'COMMON.LAYOUT.LOADING' | translate }}
        </span>
        <mat-spinner [diameter]="35" [attr.aria-hidden]="true"> </mat-spinner>
      </div>
    }
  }
  @if (attachmentsToAdd$ | async; as attachmentsToAdd) {
    <div class="attachments-section-wrapper">
      <div class="attachments-list-label">
        {{ 'COMMON.LABEL.ATTACHMENTS' | translate }}
      </div>
      <div class="attachments-list">
        @for (item of attachmentsToAdd; track item; let attachmentIndex = $index) {
          <div class="attachment-asset">
            @if (item.uploaded && item.userAsset?.id) {
              <span class="blued-text">{{ item.fileOriginalName }}</span>
              <button
                class="remove-attachment-button"
                type="button"
                (click)="removeAsset(item.userAsset)"
                [attr.aria-label]="'COMMON.ACTION.REMOVE' | translate">
                <mat-icon aria-hidden="true">close</mat-icon>
              </button>
            }
            @if (item.loading) {
              <div class="loading-wrapper" role="status" aria-live="polite">
                <span class="in-progress-text">
                  {{ item.fileOriginalName }}
                </span>
                <span class="sr-only">
                  {{ 'COMMON.LAYOUT.LOADING' | translate }}
                </span>
                <mat-spinner [diameter]="25" [attr.aria-hidden]="true"> </mat-spinner>
              </div>
            }
            @if (item.error) {
              <span class="error-text">
                {{ item.fileOriginalName }}
              </span>
              <mat-icon fontSet="material-icons-outlined" class="error-icon">error</mat-icon>
            }
          </div>
        }
      </div>
    </div>
  }
  <input
    id="attachments-input"
    class="file-input-field"
    type="file"
    #fileUploadInput
    multiple
    tabindex="-1"
    (change)="uploadFiles($event)"
    [attr.aria-hidden]="true" />
</div>
